import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urljoin from 'url-join';

import { AccountApi } from '../../../api/account-api';
import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../pool-autocomplete-list';
import { Contact } from '../../../models/contact';
import { ContactDetails } from '../contact-details';
import { CustomerDetailTabs } from '../../../../features/contacts-and-organizations/customer/customer-detail/models/enum/customer-detail-tabs';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList, PoolFormAutocompleteListProps } from '../../abstract/form-autocomplete-list';
import { ROUTES } from '../../../../features/sidebar-menu/navigation-drawer/routes';
import { SupplierDetailTabs } from '../../../../features/contacts-and-organizations/supplier/supplier-detail/models/enum/supplier-detail-tabs';
import { User } from '../../../models/user';

export interface PartialAccountContact
    extends BaseAutocompleteSuggestionType,
        Pick<Contact, 'fullName' | 'positionName'> {}

interface PoolAccountContactAutocompleteListReduxProps {
    user: User;
    helperText?: ReactNode | null;
}

interface PoolAccountContactAutocompleteListOwnProps
    extends Omit<PoolFormAutocompleteListProps<Contact, PartialAccountContact>, 'items'> {
    accountId?: number;
    /** Whose people these are; the link below the list adds one there. */
    accountType?: 'customer' | 'supplier';
    /** The person picked so far, marked in the list while another is chosen (R-192). */
    currentContactId?: number;
    showTopics?: boolean;
}

type PoolAccountContactAutocompleteListProps = PoolAccountContactAutocompleteListOwnProps &
    PoolAccountContactAutocompleteListReduxProps;

class PoolAccountContactAutocompleteListBase extends PoolFormAutocompleteList<
    Contact,
    PartialAccountContact,
    PoolAccountContactAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new AccountApi(API_BASE_URL);
    private apiAbortController?: AbortController;

    /*
     * Adds a person to the account the list belongs to, on its employees tab.
     * It said "Kunden" and opened a new member, for a supplier's people as
     * well (R-200).
     */
    public footer = () => {
        const { accountId, accountType = 'customer' } = this.props;
        const routes = ROUTES.contactsAndOrganizations;
        const isSupplier = 'supplier' === accountType;
        const url = accountId
            ? urljoin(
                  BASE_URL,
                  (isSupplier ? routes.suppliers.detail : routes.customers.detail).replace(':id', String(accountId)),
                  isSupplier ? SupplierDetailTabs.Employees : CustomerDetailTabs.Employee
              )
            : urljoin(BASE_URL, routes.contacts.new);

        return (
            hasRole(this.actionRoles, this.props.user) && (
                <PoolAutocompleteListFooter text="app.contactsAndOrganizations.detail.newContactPerson" url={url} />
            )
        );
    };
    public label = (<FormattedMessage id="app.label.customer" />);
    public getSuggestionValue = (suggestion: PartialAccountContact) => suggestion && suggestion.fullName;
    public getDetailContent = (item?: PartialAccountContact) =>
        item?.id && (
            <ContactDetails
                contactId={+item.id}
                accountId={this.props.accountId}
                showTopics={this.props.showTopics}
                key={+item.id}
            />
        );

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public componentDidUpdate(prevProps: PoolAccountContactAutocompleteListProps) {
        if (this.props.accountId !== prevProps.accountId) {
            this.setState({ items: [] });
        }
    }

    public async handleValueChanged(search: string) {
        if (!this.props.accountId) {
            this.setState({ items: [] });
            return;
        }
        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.getContacts(this.props.accountId, search, this.apiAbortController.signal);
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success === true ? response.result : [];
        this.setState({ items });
    }

    public getPartial = (items: Contact[]): PartialAccountContact[] => {
        return items.map(contact => {
            return {
                id: padZeroLeft(contact.id, 5),
                fullName: contact.fullName,
                tags: contact.getTags(30),
                positionName: contact.positionName || '',
                ...(undefined !== this.props.currentContactId && contact.id === this.props.currentContactId
                    ? { current: '✓ aktuell' }
                    : {}),
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const PoolAccountContactAutocompleteList = connect(mapStateToProps)(PoolAccountContactAutocompleteListBase);
